import moment from "moment";
import "moment/locale/es";
import { HeaderEmpresaCard } from "../Components/PdfComponents/HeaderEmpresaCard";

moment.locale("es");

const nombreEmpleado = (emp) => {
  if (!emp) return "—";
  return [emp.nombre, emp.apellidoPaterno, emp.apellidoMaterno]
    .filter(Boolean)
    .join(" ");
};

const nombreCliente = (cliente) => {
  if (!cliente) return "—";
  const nombreArmado = [cliente.nombre, cliente.apellidoPaterno, cliente.apellidoMaterno]
    .filter(Boolean)
    .join(" ");
  
  return nombreArmado || cliente.razonSocial || "—";
};

const diasSemana = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

const diasActivosTexto = (diasActivos = []) => {
  const activos = diasActivos
    .map((activo, i) => (activo ? diasSemana[i] : null))
    .filter(Boolean);
  return activos.length > 0 ? activos.join(", ") : "Ninguno";
};

const nivelColor = { bajo: "#27ae60", medio: "#e67e22", alto: "#e74c3c" };

const layoutLineas = {
  hLineWidth: () => 0.5,
  vLineWidth: () => 0.5,
  hLineColor: () => "#cccccc",
  vLineColor: () => "#cccccc",
};

const layoutSinBorde = { hLineWidth: () => 0, vLineWidth: () => 0 };

const seccionEncabezado = (programa, empresa) => ({
  style: "tableExample",
  table: {
    widths: ["18%", "52%", "30%"],
    body: [
      [
        ...HeaderEmpresaCard(
          empresa,
          true,   
          false,  
          {
            border: [false, false, false, false],
            table: {
              widths: ["100%"],
              body: [
                [{ text: "Programa de Calidad", style: "subheader", alignment: "center" }],
                [{ text: programa?.nombre || "—", style: "textoChico", alignment: "center" }],
                [{ text: "Fecha:", style: "subheader", alignment: "center" }],
                [{ text: programa?.fecha ? moment(programa.fecha).format("LL") : "—", style: "textoChico", alignment: "center" }],
              ],
            },
            layout: "noBorders",
          }
        ),
      ],
    ],
  },
  layout: layoutSinBorde,
});

const seccionDatosGenerales = (programa, cc = {}) => {
  const cprog = cc.programa ?? {};
  const showDuracion  = cprog.duracionEstimada !== false;
  const showMetadatos = cprog.metadatos        !== false;

  const filasDuracionMeta = (showDuracion || showMetadatos) ? [[
    { text: showDuracion  ? "Duración estimada:" : "", style: "subheader", fillColor: "#f0f0f0" },
    { text: showDuracion  ? (programa.duracionEstimada ? `${programa.duracionEstimada} minutos` : "—") : "", style: "textoChico" },
    { text: showMetadatos ? "Tipo de industria:" : "", style: "subheader", fillColor: "#f0f0f0" },
    { text: showMetadatos ? (programa.metadatos?.tipoIndustria || "—") : "", style: "textoChico" },
  ]] : [];

  return {
    style: "tableExample",
    stack: [
      { text: "Datos Generales", style: "tituloSeccion" },
      {
        table: {
          widths: ["20%", "30%", "20%", "30%"],
          body: [
            [
              { text: "Cliente:", style: "subheader", fillColor: "#f0f0f0" },
              { text: nombreCliente(programa.clienteId), style: "textoChico" },
              { text: "Proyecto:", style: "subheader", fillColor: "#f0f0f0" },
              { text: programa.proyectoId?.nombre || "—", style: "textoChico" },
            ],
            ...filasDuracionMeta,
            ...(programa.descripcion
              ? [[
                  { text: "Descripción:", style: "subheader", fillColor: "#f0f0f0" },
                  { text: programa.descripcion, style: "textoChico", colSpan: 3 },
                  {}, {},
                ]]
              : []),
            ...(programa.observaciones
              ? [[
                  { text: "Observaciones:", style: "subheader", fillColor: "#f0f0f0" },
                  { text: programa.observaciones, style: "textoChico", colSpan: 3 },
                  {}, {},
                ]]
              : []),
          ],
        },
        layout: layoutLineas,
      },
    ],
  };
};

const seccionEquipo = (programa) => {
  const filasColaboradores = (programa.colaboradores || []).map((col) => [
    { text: nombreEmpleado(col.empleadoId), style: "textoChico" },
    { text: col.rol || "—", style: "textoChico" },
  ]);

  return {
    style: "tableExample",
    stack: [
      { text: "Equipo del Programa", style: "tituloSeccion" },
      {
        table: {
          widths: ["25%", "75%"],
          body: [
            [
              { text: "Supervisor:", style: "subheader", fillColor: "#f0f0f0" },
              { text: nombreEmpleado(programa.supervisorId), style: "textoChico" },
            ],
          ],
        },
        layout: layoutLineas,
        margin: [0, 4, 0, 4],
      },
      ...(filasColaboradores.length > 0
        ? [
            { text: "Colaboradores", style: "subheader", margin: [0, 4, 0, 2] },
            {
              table: {
                widths: ["50%", "50%"],
                body: [
                  [
                    { text: "Nombre", style: "tituloTabla" },
                    { text: "Rol", style: "tituloTabla" },
                  ],
                  ...filasColaboradores,
                ],
              },
              layout: layoutLineas,
            },
          ]
        : []),
    ],
  };
};

const seccionPunto = (punto, indexPunto, cc = {}) => {
  const cp = cc.punto ?? {};
  const showPasos               = cp.pasos               !== false;
  const showCriterioTolerancia  = cp.criterioTolerancia  !== false;
  const showCriterioReferencias = cp.criterioReferencias !== false;
  const showRiesgos             = cp.riesgos             !== false;

  const pasos = punto.procedimiento?.pasos || [];
  const criterios = punto.criterios || [];
  const riesgos = punto.riesgos || [];
  const imagenKey = punto.imagenBase64?.base64Completa
    ? `punto_${punto._id}`
    : null;

  return {
    stack: [
      {
        table: {
          widths: ["100%"],
          body: [[
            {
              text: `  ${indexPunto + 1}. ${punto.nombre}`,
              style: "tituloPunto",
              fillColor: "#6b6b6b",
              color: "#ffffff",
              margin: [4, 3, 4, 3],
            },
          ]],
        },
        layout: layoutSinBorde,
        margin: [0, 6, 0, 3],
      },

      ...(punto.descripcion
        ? [
            { text: "Descripción:", style: "subheader", margin: [0, 2, 0, 1] },
            { text: punto.descripcion, style: "textoChico", margin: [4, 0, 0, 4] },
          ]
        : []),
      ...(punto.observaciones
        ? [
            { text: "Observaciones:", style: "subheader", margin: [0, 2, 0, 1] },
            { text: punto.observaciones, style: "textoChico", margin: [4, 0, 0, 4] },
          ]
        : []),
        ...(imagenKey
        ? [
            { text: "Imagen de referencia:", style: "subheader", margin: [0, 4, 0, 2] },
            {
              image: imagenKey,
              width: 200,
              margin: [0, 0, 0, 6],
            },
          ]
        : []),

      // Pasos del procedimiento
      ...(showPasos && pasos.length > 0
        ? [
            { text: "Procedimiento", style: "subheader", margin: [0, 4, 0, 2] },
            {
              table: {
                widths: ["6%", "94%"],
                body: pasos
                  .sort((a, b) => a.orden - b.orden)
                  .map((paso, i) => [
                    { text: `${i + 1}.`, style: "textoChico", alignment: "right", color: "#555555" },
                    { text: paso.descripcion, style: "textoChico" },
                  ]),
              },
              layout: layoutSinBorde,
              margin: [8, 0, 0, 6],
            },
          ]
        : []),

      // Criterios de aceptación
      ...(criterios.length > 0
        ? (() => {
            const colWidths = showCriterioTolerancia && showCriterioReferencias
              ? ["40%", "20%", "20%", "20%"]
              : showCriterioTolerancia
                ? ["50%", "25%", "25%"]
                : showCriterioReferencias
                  ? ["50%", "25%", "25%"]
                  : ["65%", "35%"];

            const headerRow = [
              { text: "Criterio", style: "tituloTabla" },
              { text: "Tipo", style: "tituloTabla", alignment: "center" },
              ...(showCriterioTolerancia  ? [{ text: "Tolerancia",  style: "tituloTabla", alignment: "center" }] : []),
              ...(showCriterioReferencias ? [{ text: "Referencias", style: "tituloTabla", alignment: "center" }] : []),
            ];

            const dataRows = criterios.map((c) => [
              { text: c.criterio || "—", style: "textoChico" },
              { text: c.tipo || "—", style: "textoChico", alignment: "center" },
              ...(showCriterioTolerancia  ? [{ text: c.tolerancia || "—", style: "textoChico", alignment: "center" }] : []),
              ...(showCriterioReferencias ? [{
                text: (c.referencias || []).map((r) => r.titulo || r.url || "").filter(Boolean).join(", ") || "—",
                style: "textoChico",
                alignment: "center",
              }] : []),
            ]);

            return [
              { text: "Criterios de aceptación", style: "subheader", margin: [0, 4, 0, 2] },
              {
                table: { widths: colWidths, body: [headerRow, ...dataRows] },
                layout: layoutLineas,
                margin: [0, 0, 0, 6],
              },
            ];
          })()
        : []),

      // Riesgos
      ...(showRiesgos && riesgos.length > 0
        ? [
            { text: "Riesgos", style: "subheader", margin: [0, 4, 0, 2] },
            {
              table: {
                widths: ["50%", "15%", "35%"],
                body: [
                  [
                    { text: "Descripción", style: "tituloTabla" },
                    { text: "Nivel", style: "tituloTabla", alignment: "center" },
                    { text: "Medidas de mitigación", style: "tituloTabla" },
                  ],
                  ...riesgos.map((r) => [
                    { text: r.descripcion || "—", style: "textoChico" },
                    {
                      text: r.nivel
                        ? r.nivel.charAt(0).toUpperCase() + r.nivel.slice(1)
                        : "—",
                      style: "textoChico",
                      alignment: "center",
                      color: nivelColor[r.nivel] || "#000000",
                      bold: true,
                    },
                    { text: r.medidasMitigacion || "—", style: "textoChico" },
                  ]),
                ],
              },
              layout: layoutLineas,
              margin: [0, 0, 0, 6],
            },
          ]
        : []),
    ],
  };
};

const seccionProceso = (proceso, indexProceso, cc = {}) => {
  const cproc = cc.proceso ?? {};
  const showToleranciaRetardo = cproc.toleranciaRetardo !== false;
  const showRequireAprobacion = cproc.requireAprobacion !== false;
  const showRequirePresencia  = cproc.requirePresencia  !== false;
  const showDuracionEstimada  = cproc.duracionEstimada  !== false;

  const reglas = proceso.reglasProgramacion || {};

  const filasResponsables = (proceso.responsables || []).map((r) => [
    { text: nombreEmpleado(r.empleadoId), style: "textoChico" },
    { text: r.rol || "—", style: "textoChico" },
    { text: r.esPrincipal ? "Sí" : "No", style: "textoChico", alignment: "center" },
  ]);

  const filasHorarios = (reglas.horariosEjecucion || []).map((h) => [
    { text: h.horaInicio || "—", style: "textoChico", alignment: "center" },
    { text: h.horaFin || "—", style: "textoChico", alignment: "center" },
    { text: h.descripcion || "—", style: "textoChico" },
  ]);

  const seccionesPuntos = (proceso.puntos || []).map((punto, i) =>
    seccionPunto(punto, i, cc)
  );

  return {
    stack: [
      // Cabecera del proceso
      {
        table: {
          widths: ["100%"],
          body: [[
            {
              text: `${indexProceso + 1}. ${proceso.nombre}`,
              style: "tituloProceso",
              fillColor: "#4a4a4a",
              color: "#ffffff",
              margin: [6, 4, 6, 4],
            },
          ]],
        },
        layout: layoutSinBorde,
        margin: [0, 12, 0, 4],
      },

      // Info general
      {
        table: {
          widths: ["20%", "30%", "20%", "30%"],
          body: [
            [
              { text: "Prioridad:", style: "subheader", fillColor: "#f5f5f5" },
              { text: proceso.prioridad ?? "—", style: "textoChico" },
              { text: showDuracionEstimada ? "Duración estimada:" : "", style: "subheader", fillColor: "#f5f5f5" },
              { text: showDuracionEstimada ? (reglas.duracionEstimada ? `${reglas.duracionEstimada} min` : "—") : "", style: "textoChico" },
            ],
            [
              { text: "Días activos:", style: "subheader", fillColor: "#f5f5f5" },
              { text: diasActivosTexto(reglas.diasActivos), style: "textoChico", colSpan: 3 },
              {}, {},
            ],
            ...(showToleranciaRetardo || showRequireAprobacion ? [[
              { text: showToleranciaRetardo ? "Tolerancia retardo:" : "", style: "subheader", fillColor: "#f5f5f5" },
              { text: showToleranciaRetardo ? (reglas.toleranciaRetardo ? `${reglas.toleranciaRetardo} min` : "0 min") : "", style: "textoChico" },
              { text: showRequireAprobacion ? "Req. aprobación:" : "", style: "subheader", fillColor: "#f5f5f5" },
              { text: showRequireAprobacion ? (reglas.requireAprobacion ? "Sí" : "No") : "", style: "textoChico" },
            ]] : []),
            [
              { text: showRequirePresencia ? "Req. presencia:" : "", style: "subheader", fillColor: "#f5f5f5" },
              { text: showRequirePresencia ? (reglas.requirePresencia ? "Sí" : "No") : "", style: "textoChico" },
              { text: "Carga de imagen:", style: "subheader", fillColor: "#f5f5f5" },
              { text: proceso.activarCargaImagen ? "Sí" : "No", style: "textoChico" },
            ],
          ],
        },
        layout: layoutLineas,
        margin: [0, 0, 0, 4],
      },

      ...(proceso.descripcion
        ? [
            { text: "Descripción:", style: "subheader", margin: [0, 4, 0, 1] },
            { text: proceso.descripcion, style: "textoChico", margin: [0, 0, 0, 4] },
          ]
        : []),
      ...(proceso.observaciones
        ? [
            { text: "Observaciones:", style: "subheader", margin: [0, 2, 0, 1] },
            { text: proceso.observaciones, style: "textoChico", margin: [0, 0, 0, 4] },
          ]
        : []),

      // Responsables
      ...(filasResponsables.length > 0
        ? [
            { text: "Responsables", style: "subheader", margin: [0, 4, 0, 2] },
            {
              table: {
                widths: ["45%", "35%", "20%"],
                body: [
                  [
                    { text: "Nombre", style: "tituloTabla" },
                    { text: "Rol", style: "tituloTabla" },
                    { text: "Principal", style: "tituloTabla", alignment: "center" },
                  ],
                  ...filasResponsables,
                ],
              },
              layout: layoutLineas,
              margin: [0, 0, 0, 4],
            },
          ]
        : []),

      // Horarios
      ...(filasHorarios.length > 0
        ? [
            { text: "Horarios de ejecución", style: "subheader", margin: [0, 4, 0, 2] },
            {
              table: {
                widths: ["20%", "20%", "60%"],
                body: [
                  [
                    { text: "Hora inicio", style: "tituloTabla", alignment: "center" },
                    { text: "Hora fin", style: "tituloTabla", alignment: "center" },
                    { text: "Descripción", style: "tituloTabla" },
                  ],
                  ...filasHorarios,
                ],
              },
              layout: layoutLineas,
              margin: [0, 0, 0, 4],
            },
          ]
        : []),

      // Puntos
      ...(seccionesPuntos.length > 0
        ? [
            { text: `Puntos del proceso (${seccionesPuntos.length})`, style: "tituloSeccion", margin: [0, 8, 0, 4] },
            ...seccionesPuntos,
          ]
        : [
            { text: "Sin puntos registrados.", style: "textoChico", color: "#999999", margin: [0, 4, 0, 4] },
          ]),
    ],
  };
};

const programaPdf = (datosRaw) => {
  const programa      = datosRaw?.programa || datosRaw;
  const procesos      = datosRaw?.procesos || programa?.procesos || [];
  const empresa       = datosRaw?.empresa  || programa?.empresa  || {};
  const camposCalidad = datosRaw?.camposCalidad ?? {};

  const seccionesProcesos = procesos.map((proc, i) => seccionProceso(proc, i, camposCalidad));
  const logoBase64 = empresa?.logo?.base64Completa || null

  const imagenesPuntos = {};
  procesos.forEach((proc) => {
    (proc.puntos || []).forEach((punto) => {
      if (punto.imagenBase64?.base64Completa) {
        imagenesPuntos[`punto_${punto._id}`] = punto.imagenBase64.base64Completa;
      }
    });
  });

  const images = {
    ...(logoBase64 ? { logo: logoBase64 } : {}),
    ...imagenesPuntos,
  };


  return {
    info: {
      title: `Programa - ${programa?.nombre || "Sin Nombre"}`,
    },
    content: [
      seccionEncabezado(programa, empresa),
      { canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: "#4a4a4a" }], margin: [0, 4, 0, 8] },
      seccionDatosGenerales(programa, camposCalidad),
      seccionEquipo(programa),
      { canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: "#cccccc" }], margin: [0, 4, 0, 8] },
      { text: `Procesos del Programa (${procesos.length})`, style: "tituloSeccion", margin: [0, 0, 0, 6] },
      ...seccionesProcesos,
    ],
    ...(Object.keys(images).length > 0 ? { images } : {}),
    styles: {
      tituloPrincipal: { fontSize: 18, bold: true, color: "#4a4a4a" },
      subtitulo: { fontSize: 10, italics: true },
      tituloSeccion: { fontSize: 11, bold: true, color: "#4a4a4a", margin: [0, 6, 0, 3] },
      tituloProceso: { fontSize: 10, bold: true },
      tituloPunto: { fontSize: 9, bold: true },
      header: { fontSize: 9, bold: true },
      subheader: { fontSize: 8, bold: true },
      tableExample: { margin: [0, 4, 0, 4] },
      tableHeader: { bold: true, fontSize: 12, color: "black", alignment: "center" },
      tituloTabla: { fontSize: 7, bold: true, fillColor: "#cccccc", alignment: "center" },
      textoChico: { fontSize: 8 },
    },
    pageSize: "LETTER",
    pageMargins: [30, 40, 30, 40],
  };
};

export default programaPdf;